function textOr(value, fallback) {
  const next = typeof value === "string" ? value.trim() : "";
  return next || fallback;
}

function shown(value, fallback = true) {
  if (value === undefined || value === null || value === "") return fallback;
  return value !== false && value !== "false";
}

function oneOf(value, allowed, fallback) {
  const next = String(value || "");
  return allowed.includes(next) ? next : fallback;
}

const RATIOS = {
  portrait: "3 / 4",
  square: "1 / 1",
  landscape: "4 / 3",
};

const TITLE_SIZES = {
  small: "28px",
  medium: "clamp(32px, 4vw, 44px)",
  large: "clamp(40px, 5vw, 56px)",
};

export function collectionPageSettings(settings = {}) {
  const columns = Number(oneOf(settings.collectionColumns, ["2", "3", "4", "5"], "4"));
  const columnsMobile = Number(oneOf(settings.collectionColumnsMobile, ["1", "2"], "2"));
  const indexColumns = Number(oneOf(settings.collectionIndexColumns, ["2", "3", "4"], "3"));
  const indexColumnsMobile = Number(oneOf(settings.collectionIndexColumnsMobile, ["1", "2"], "2"));
  const imageRatio = oneOf(settings.collectionImageRatio, ["portrait", "square", "landscape"], "portrait");
  const imageFit = oneOf(settings.collectionImageFit, ["cover", "contain"], "cover");
  const titleSize = oneOf(settings.collectionTitleSize, ["small", "medium", "large"], "medium");
  return {
    background: settings.collectionBackground || "#ffffff",
    headingColor: settings.collectionHeadingColor || "#121212",
    textColor: settings.collectionTextColor || "#444444",
    titleAlign: oneOf(settings.collectionTitleAlign, ["left", "center"], "left"),
    titleSize,
    showDescription: shown(settings.collectionShowDescription, true),
    showBreadcrumbs: shown(settings.collectionShowBreadcrumbs, true),
    showCount: shown(settings.collectionShowCount, true),
    showFilter: shown(settings.collectionShowFilter, true),
    showSort: shown(settings.collectionShowSort, true),
    filterStyle: oneOf(settings.collectionFilterStyle, ["drawer", "sidebar"], "drawer"),
    pagination: oneOf(settings.collectionPagination, ["pages", "more"], "pages"),
    perPage: Number(oneOf(settings.collectionPerPage, ["8", "12", "16", "24"], "16")),
    columns,
    columnsMobile,
    imageRatio,
    imageFit,
    showHoverImage: shown(settings.collectionShowHoverImage, true),
    showVendor: shown(settings.collectionShowVendor, true),
    showSwatches: shown(settings.collectionShowSwatches, true),
    showWishlist: shown(settings.collectionShowWishlist, true),
    showSaleBadge: shown(settings.collectionShowSaleBadge, true),
    showSoldOut: shown(settings.collectionShowSoldOut, true),
    saleLabel: textOr(settings.collectionSaleLabel, "Sale"),
    saleBackground: settings.collectionSaleBackground || "#ffffff",
    saleText: settings.collectionSaleText || "#121212",
    soldLabel: textOr(settings.collectionSoldLabel, "Sold out"),
    soldBackground: settings.collectionSoldBackground || "#ffffff",
    soldText: settings.collectionSoldText || "#121212",
    nameColor: settings.collectionNameColor || "#121212",
    priceColor: settings.collectionPriceColor || "#121212",
    oldPriceColor: settings.collectionOldPriceColor || "#6b7280",
    vendorColor: settings.collectionVendorColor || "#6b7280",
    showQuickAdd: shown(settings.collectionShowQuickAdd, true),
    addLabel: textOr(settings.collectionAddLabel, "Add to cart"),
    chooseLabel: textOr(settings.collectionChooseLabel, "Choose options"),
    quickBackground: settings.collectionQuickBackground || "#ffffff",
    quickText: settings.collectionQuickText || "#121212",
    quickBorder: settings.collectionQuickBorder || "#121212",
    indexColumns,
    indexColumnsMobile,
    indexShowCount: shown(settings.collectionIndexShowCount, true),
    indexCaption: settings.collectionIndexCaption || "#ffffff",
    indexOverlay: settings.collectionIndexOverlay || "#000000",
  };
}

/**
 * The Collections page (every category) has its own look. Each setting falls
 * back to the Category page's value, so stores that never set it look the same.
 */
export function collectionsPageSettings(settings = {}) {
  const category = collectionPageSettings(settings);
  return {
    ...category,
    background: settings.collectionIndexBackground || category.background,
    headingColor: settings.collectionIndexHeadingColor || category.headingColor,
    titleAlign: oneOf(settings.collectionIndexTitleAlign, ["left", "center"], category.titleAlign),
    showBreadcrumbs: shown(settings.collectionIndexShowBreadcrumbs, category.showBreadcrumbs),
    pagination: oneOf(settings.collectionIndexPagination, ["pages", "more"], category.pagination),
    perPage: Number(oneOf(settings.collectionIndexPerPage, ["8", "12", "16", "24"], String(category.perPage))),
  };
}

export function collectionPageStyle(look) {
  return {
    "--col-bg": look.background,
    "--col-heading": look.headingColor,
    "--col-text": look.textColor,
    "--col-align": look.titleAlign,
    "--col-title-size": TITLE_SIZES[look.titleSize] || TITLE_SIZES.medium,
    "--col-cols": String(look.columns),
    "--col-cols-mobile": String(look.columnsMobile),
    "--col-index-cols": String(look.indexColumns),
    "--col-index-mobile": String(look.indexColumnsMobile),
    "--col-ratio": RATIOS[look.imageRatio] || RATIOS.portrait,
    "--col-fit": look.imageFit,
    "--col-fit-pad": look.imageFit === "contain" ? "8px" : "0px",
    "--col-name": look.nameColor,
    "--col-price": look.priceColor,
    "--col-old": look.oldPriceColor,
    "--col-vendor": look.vendorColor,
    "--col-quick-bg": look.quickBackground,
    "--col-quick-text": look.quickText,
    "--col-quick-border": look.quickBorder,
    "--col-caption": look.indexCaption,
  };
}
